import { useCustomForm } from "common/hooks/src/api/useCustomForm";
import { Button } from "components/button";
import { CardWithTitle } from "components/card/CardWithTitle";
import { InputField, ResizableInputField } from "components/fields/InputField";
import {
  useGetFamily,
  useUpdateFamily,
  useArchiveFamily,
  useRemoveFamilyMember,
} from "modules/family/api";
import { CreateFamilyPayload } from "modules/family/types/family.interface";
import { FormEvent, useEffect, useState } from "react";
import "react-datepicker/dist/react-datepicker.css";
import { useParams } from "react-router-dom";
import { MdClose } from "react-icons/md";

const UpdateFamily = () => {
  const id = useParams<{ id: string | undefined }>().id;
  const { data: family, refetch } = useGetFamily(id);
  const { mutate } = useUpdateFamily(id);
  const { mutate: archiveFamily } = useArchiveFamily();
  const { mutate: removeMember } = useRemoveFamilyMember(id);

  const { formValues, setFormValues } = useCustomForm<CreateFamilyPayload>({
    name: "",
    description: "",
  });

  const isSubmitDisabled = !formValues.name || formValues.name === "";

  const handleSubmit = (e: FormEvent) => {
    if (isSubmitDisabled) return;

    e.preventDefault();

    mutate({
      ...formValues,
    });
  };

  const handleRemoveMember = (clientId: string) => {
    removeMember(clientId, {
      onSuccess: () => {
        refetch();
      },
    });
  };

  const [firstRender, setFirstRender] = useState(true);

  useEffect(() => {
    if (family && firstRender) {
      setFormValues({
        name: family.name,
        description: family.description || "",
      });
      setFirstRender(false);
    }
  }, [firstRender, family, setFormValues]);

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      {/* Solde de la famille */}
      {family && (
        <CardWithTitle title="Solde de la famille">
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
            <div className="rounded-lg bg-gray-50 p-4 dark:bg-navy-700">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Membres
              </p>
              <p className="text-2xl font-bold text-navy-700 dark:text-white">
                {family.members?.length || 0}
              </p>
            </div>
            <div className="rounded-lg bg-gray-50 p-4 dark:bg-navy-700">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Solde dû total
              </p>
              <p
                className={`text-2xl font-bold ${
                  family.totalAmountDue > 0 ? "text-red-500" : "text-green-500"
                }`}
              >
                {(family.totalAmountDue || 0).toFixed(2)} €
              </p>
            </div>
            <div className="rounded-lg bg-gray-50 p-4 dark:bg-navy-700">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Total payé
              </p>
              <p className="text-2xl font-bold text-green-500">
                {(family.totalAmountPaid || 0).toFixed(2)} €
              </p>
            </div>
          </div>
        </CardWithTitle>
      )}

      {/* Membres de la famille */}
      {family && family.members && family.members.length > 0 && (
        <CardWithTitle title="Membres de la famille">
          <div className="space-y-2">
            {family.members.map((member) => (
              <div
                key={member._id}
                className="flex items-center justify-between rounded-lg bg-gray-50 p-3 dark:bg-navy-700"
              >
                <div>
                  <p className="font-bold text-navy-700 dark:text-white">
                    {member.name}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {member.email?.[0]?.value || "Pas d'email"} •{" "}
                    <span
                      className={
                        (member.amountDue || 0) > 0
                          ? "text-red-500"
                          : "text-green-500"
                      }
                    >
                      {(member.amountDue || 0).toFixed(2)} € dû
                    </span>
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <a
                    href={`/admin/client/${member._id}`}
                    className="text-sm text-brand-500 hover:underline"
                  >
                    Voir
                  </a>
                  <button
                    type="button"
                    onClick={() => handleRemoveMember(member._id)}
                    className="rounded-full p-1 text-red-500 hover:bg-red-100"
                    title="Retirer de la famille"
                  >
                    <MdClose size={18} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </CardWithTitle>
      )}

      <CardWithTitle title="Information de la famille">
        <div className="grid grid-cols-1 gap-5">
          <InputField
            variant="auth"
            extra="mb-3"
            label="Nom de la famille*"
            placeholder="Famille Dupont"
            id="name"
            type="text"
            value={formValues.name}
            onChange={(e) =>
              setFormValues({
                ...formValues,
                name: e.target.value,
              })
            }
          />
          <ResizableInputField
            variant="auth"
            extra="mb-3"
            label="Description"
            placeholder="Description de la famille..."
            id="description"
            value={formValues.description}
            onChange={(e) =>
              setFormValues({
                ...formValues,
                description: e.target.value,
              })
            }
          />
        </div>
      </CardWithTitle>
      <div className="mt-2 flex w-full flex-col justify-between md:flex-row">
        <div className="mb-2 w-full md:mb-0 md:max-w-xs">
          <Button onClick={() => archiveFamily(id!)} variant="alert">
            Archiver la famille
          </Button>
        </div>
        <div className="w-full md:max-w-xs">
          <Button onClick={handleSubmit} disabled={isSubmitDisabled}>
            Mettre à jour la famille
          </Button>
        </div>
      </div>
    </form>
  );
};

export default UpdateFamily;
